import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  UserMinus,
  UserPlus,
  Briefcase,
  Target,
  FileText,
  Package,
  Boxes,
  IndianRupee,
  Calendar,
  TrendingUp,
  Ticket
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fetchAdminDashboardStats } from '../../api/dashboardApi';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
  Area,
  Cell
} from 'recharts';
import PermissionGate from '../../components/common/PermissionGate/PermissionGate';
import { formatIndianAmount, formatIndianNumber } from '../../utils/amountFormatter';

const AdminDashboard = () => {
  const navigate = useNavigate();

  // Fetch dashboard stats
  const { data: dashboardStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['adminDashboardStats'],
    queryFn: fetchAdminDashboardStats,
    staleTime: 0 // Consider data stale after 1 minute
  });

  // Mock data for demonstration (structure preserved, values updated from API)
  const stats = [
    {
      title: 'Active Employees',
      value: isLoadingStats ? '...' : (formatIndianNumber(dashboardStats?.totalEmployees) || '0'),
      icon: Users,
      color: 'bg-blue-500',
      path: '/employee_list'
    },
    {
      title: 'Total Exits',
      value: isLoadingStats ? '...' : (formatIndianNumber(dashboardStats?.exitEmployees) || '0'),
      icon: UserMinus,
      color: 'bg-red-500',
      path: '/exit_requests'
    },
    {
      title: 'Active Jobs',
      value: isLoadingStats ? '...' : (formatIndianNumber(dashboardStats?.activeJobs) || '0'),
      icon: Briefcase,
      color: 'bg-indigo-500',
      path: '/job_posts'
    },
    {
      title: 'Total Candidates',
      value: isLoadingStats ? '...' : (formatIndianNumber(dashboardStats?.totalCandidates) || '0'),
      icon: UserPlus,
      color: 'bg-cyan-500',
      path: '/hrms/recruitment?tab=candidates'
    },
    {
      title: 'Leads',
      value: isLoadingStats ? '...' : (formatIndianNumber(dashboardStats?.totalLeads) || '0'),
      icon: Target,
      color: 'bg-green-500',
      path: '/lead_list_filters'
    },
    {
      title: 'Customers',
      value: isLoadingStats ? '...' : (formatIndianNumber(dashboardStats?.totalCustomers) || '0'),
      icon: Users,
      color: 'bg-teal-500',
      path: '/customer_360'
    },
    {
      title: 'Quotes',
      value: isLoadingStats ? '...' : (formatIndianNumber(dashboardStats?.totalQuotations) || '0'),
      icon: FileText,
      color: 'bg-yellow-500',
      path: '/crm/quotation-proposal/create-quotes'
    },
    {
      title: 'Invoices',
      value: isLoadingStats ? '...' : (formatIndianNumber(dashboardStats?.totalInvoices) || '0'),
      icon: Package,
      color: 'bg-purple-500',
      path: '/order_management'
    },
    {
      title: 'Inventory',
      value: isLoadingStats ? '...' : (formatIndianNumber(dashboardStats?.inventoryItems) || '0'),
      icon: Boxes,
      color: 'bg-orange-500',
      path: '/inventory_sync'
    },
    {
      title: 'Tickets',
      value: isLoadingStats ? '...' : (formatIndianNumber(dashboardStats?.totalTickets) || '0'),
      icon: Ticket,
      color: 'bg-rose-500',
      path: '/tickets_feedback'
    }
  ];

  const quickActions = [
    {
      title: 'Job Posting',
      description: 'Post new job',
      icon: UserPlus,
      color: 'bg-blue-500',
      href: '/create-job',
      action: () => navigate('/create-job'),
      permission: 'Recruitment',
      actionType: 'create'
    },
    {
      title: 'Add Employee',
      description: 'Onboard new team member',
      icon: UserPlus,
      color: 'bg-blue-500',
      href: '/add_employee',
      action: () => navigate('/add_employee'),
      permission: 'employee_management',
      actionType: 'create'
    },
    {
      title: 'Create Lead',
      description: 'Add new sales opportunity',
      icon: Target,
      color: 'bg-green-500',
      href: '/manual_lead_entry',
      action: () => navigate('/manual_lead_entry'),
      permission: 'Employee Database',
      actionType: 'create'
    },
    {
      title: 'Create Quotation',
      description: 'New quote for opportunity or customer',
      icon: IndianRupee,
      color: 'bg-yellow-500',
      href: '/crm/quotation-proposal/create-quotes',
      action: () => navigate('/crm/quotation-proposal/create-quotes'),
      permission: 'Quotation & Proposal',
      actionType: 'create'
    }
  ];


  // Employee Distribution Chart Data
  const employeeDistributionData = dashboardStats?.employeeDistribution || [
    { label: 'Full Time', value: 0 },
    { label: 'Part Time', value: 0 },
    { label: 'Contract', value: 0 },
    { label: 'Intern', value: 0 }
  ];

  // Leave Trends Chart Data
  const leaveTrendsData = dashboardStats?.leaveTrends || [];

  // Lead Trends Chart Data
  const leadTrendsData = dashboardStats?.leadTrends || [];

  // Employee Trends Chart Data
  const employeeTrendsData = dashboardStats?.employeeTrends || [];

  // Job Posting Trends (Last 6 months)
  const jobPostTrendsData = dashboardStats?.jobPostTrends || [];

  // Candidate Application Trends (Last 6 months)
  const candidateApplyTrendsData = dashboardStats?.candidateApplyTrends || [];

  // Quotation Conversion Trends (Last 6 months)
  const quotationConversionTrendsData = dashboardStats?.quotationConversionTrends || [];

  // New Customer Trends (Last 6 months)
  const customerTrendsData = dashboardStats?.customerTrends || [];

  // Revenue Trends (Last 12 months)
  const revenueTrendsData = dashboardStats?.revenueTrends || [];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Welcome back!</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <button
              key={index}
              onClick={() => navigate(stat.path)}
              className="card p-4 sm:p-5 md:p-6 text-left w-full cursor-pointer hover:shadow-md hover:-translate-y-0.5 active:scale-95 transition-all duration-200"
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1 pr-3">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 break-words">
                    {stat.title}
                  </p>
                  <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mt-1 sm:mt-2 break-words">
                    {stat.value}
                  </p>
                </div>
                <div className={`p-2 sm:p-3 rounded-lg ${stat.color} flex-shrink-0`}>
                  <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="card p-4 sm:p-5 md:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => {
            const IconComponent = action.icon;
            return (
              <PermissionGate key={index} permission={action.permission} action={action.actionType}>
                <button
                  onClick={action.action}
                  className="w-full p-3 sm:p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <div className={`p-2 rounded-lg ${action.color} flex-shrink-0`}>
                      <IconComponent className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-medium text-gray-900 break-words">{action.title}</h3>
                      <p className="text-xs text-gray-600 break-words mt-1">{action.description}</p>
                    </div>
                  </div>
                </button>
              </PermissionGate>
            );
          })}
        </div>
      </div>

      {/* Revenue Trends Section */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Revenue Trends</h2>
          <div className="flex items-center text-sm text-gray-500">
            <IndianRupee className="h-4 w-4 mr-1 text-emerald-500" />
            <span>Total Revenue</span>
          </div>
        </div>
        <div className="h-[352px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={revenueTrendsData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 60,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#d1d5db' }}
                tickLine={{ stroke: '#d1d5db' }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#d1d5db' }}
                tickLine={{ stroke: '#d1d5db' }}
                tickFormatter={(value) => `₹${formatIndianAmount(value)}`}
              />
              <Tooltip
                formatter={(value) => [`₹${formatIndianAmount(value)}`, 'Revenue']}
                labelStyle={{ color: '#374151' }}
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#10b981"
                strokeWidth={3}
                dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2, fill: '#ffffff' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Additional Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Employee Distribution Chart */}
        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Employee Distribution</h2>
            <div className="flex items-center text-sm text-gray-500">
              <Users className="h-4 w-4 mr-1" />
              <span>{formatIndianNumber(dashboardStats?.totalEmployees) || '0'} Total</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={employeeDistributionData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <Tooltip
                  formatter={(value) => [formatIndianNumber(value), 'Employees']}
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar
                  dataKey="value"
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-blue-600">
                {isLoadingStats ? '...' : formatIndianNumber(dashboardStats?.employeeDistribution?.find(d => d.label === 'Full Time')?.value || 0)}
              </div>
              <div className="text-xs sm:text-sm text-gray-600">Full-time</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-blue-500">
                {isLoadingStats ? '...' : formatIndianNumber(dashboardStats?.employeeDistribution?.find(d => d.label === 'Part Time')?.value || 0)}
              </div>
              <div className="text-xs sm:text-sm text-gray-600">Part-time</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-blue-400">
                {isLoadingStats ? '...' : formatIndianNumber(dashboardStats?.employeeDistribution?.find(d => d.label === 'Contract')?.value || 0)}
              </div>
              <div className="text-xs sm:text-sm text-gray-600">Contract</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-blue-300">
                {isLoadingStats ? '...' : formatIndianNumber(dashboardStats?.employeeDistribution?.find(d => d.label === 'Intern')?.value || 0)}
              </div>
              <div className="text-xs sm:text-sm text-gray-600">Intern</div>
            </div>
          </div>
        </div>

        {/* Leave Trends Chart */}
        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Leaves</h2>
            <div className="flex items-center text-sm text-gray-500">
              <Calendar className="h-4 w-4 mr-1" />
              <span>Approved Requests</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={leaveTrendsData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <Tooltip
                  formatter={(value) => [formatIndianNumber(value), 'Approved Leaves']}
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar
                  dataKey="value"
                  fill="#f97316"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4 text-sm">
            <div className="text-center p-2 bg-orange-50 rounded-lg">
              <div className="text-lg sm:text-xl font-bold text-orange-600">
                {isLoadingStats ? '...' : formatIndianNumber(dashboardStats?.leavesThisMonth || 0)}
              </div>
              <div className="text-xs text-gray-600">This Month</div>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-lg sm:text-xl font-bold text-gray-600">
                {isLoadingStats ? '...' : formatIndianNumber(dashboardStats?.leavesLastMonth || 0)}
              </div>
              <div className="text-xs text-gray-600">Last Month</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Trends Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Lead Trends Chart */}
        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Lead Trends (Monthly)</h2>
            <div className="flex items-center text-sm text-gray-500">
              <Target className="h-4 w-4 mr-1" />
              <span>{formatIndianNumber(dashboardStats?.totalLeads) || '0'} Total</span>
            </div>
          </div>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={leadTrendsData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <Tooltip
                  formatter={(value) => [formatIndianNumber(value), 'Leads']}
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#16a34a"
                  strokeWidth={3}
                  dot={{ fill: '#16a34a', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#16a34a', strokeWidth: 2, fill: '#ffffff' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Employee Trends Chart */}
        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Employee Trends</h2>
            <div className="flex items-center text-sm text-gray-500">
              <TrendingUp className="h-4 w-4 mr-1" />
              <span>Joining & Exit</span>
            </div>
          </div>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={employeeTrendsData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <Tooltip
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="joined"
                  stroke="#2563eb"
                  strokeWidth={3}
                  dot={{ fill: '#2563eb', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#2563eb', strokeWidth: 2, fill: '#ffffff' }}
                  name="Joined"
                />
                <Line
                  type="monotone"
                  dataKey="exited"
                  stroke="#dc2626"
                  strokeWidth={3}
                  dot={{ fill: '#dc2626', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#dc2626', strokeWidth: 2, fill: '#ffffff' }}
                  name="Exited"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recruitment Trends Section (Separate Charts) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Job Posting Trends */}
        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Job Trends</h2>
            <div className="flex items-center text-sm text-gray-500">
              <Briefcase className="h-4 w-4 mr-1" />
              <span>Last 6 Months</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={jobPostTrendsData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <Tooltip
                  formatter={(value) => [formatIndianNumber(value), 'Jobs Posted']}
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar
                  dataKey="value"
                  fill="#4f46e5"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Candidate Application Trends */}
        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Application Trends</h2>
            <div className="flex items-center text-sm text-gray-500">
              <UserPlus className="h-4 w-4 mr-1" />
              <span>Last 6 Months</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={candidateApplyTrendsData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <Tooltip
                  formatter={(value) => [formatIndianNumber(value), 'Applications']}
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar
                  dataKey="value"
                  fill="#06b6d4"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Bottom Business Trends Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Quotation Conversion Trends */}
        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Conversion</h2>
            <div className="flex items-center text-sm text-gray-500">
              <FileText className="h-4 w-4 mr-1" />
              <span>Total Leads vs Converted (Last 6 Months)</span>
            </div>
          </div>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={quotationConversionTrendsData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <Tooltip
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value) => formatIndianNumber(value)}
                />
                <Legend />
                <Bar
                  dataKey="total"
                  fill="#3b82f6"
                  radius={[2, 2, 0, 0]}
                  name="Total Leads"
                />
                <Bar
                  dataKey="converted"
                  fill="#16a34a"
                  radius={[2, 2, 0, 0]}
                  name="Converted to Opportunity"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* New Customer Trends */}
        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Customer Trends</h2>
            <div className="flex items-center text-sm text-gray-500">
              <Users className="h-4 w-4 mr-1" />
              <span>By Created Date, Last 6 Months</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={customerTrendsData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <Tooltip
                  formatter={(value) => [formatIndianNumber(value), 'New Customers']}
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar
                  dataKey="value"
                  fill="#14b8a6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
